import { logger } from '@beelday/common'
import { Config } from 'common/config'
import { RootState } from 'common/redux'
import { MutableRefObject, useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import type { MediaProgress } from 'room/common/media-progress'
import type { ConsoleEffects } from './console-effects'
import {
	ConsoleMessage,
	isProjectorHere,
	PresenterCommand,
	readProjectorMessage,
} from './model/presenter-messages'
import { bridgeChannelName } from './model/presenter-url'
import { openBroadcastChannel } from './replication/channel'
import { startHost } from './replication/host'
import type { Relay } from './replication/relay'
import { ROOT_SYNC_POLICY } from './root-sync-policy'
import type { SendConsoleEvent } from './use-presentation-machine'
import { webLocks } from './window-management'

const log = logger.create('presenter-mode')

export type ProjectorOutbox = MutableRefObject<
	(message: ConsoleMessage) => void
>

type Options = {
	liveSessionId: string
	/** The running presentation's id; no link without one. */
	presentation: string | undefined
	relay: Relay | null
	effects: ConsoleEffects
	send: SendConsoleEvent
	/** Filled with the way to the presentation window while the link runs. */
	outbox: ProjectorOutbox
	onMedia: (media: MediaProgress | null) => void
	onCommand: (command: PresenterCommand) => void
	/** The presentation window's recordings cannot use the chosen speakers. */
	onSpeakers: (fellBack: boolean) => void
}

const isWindowProxy = (source: MessageEventSource | null): source is Window =>
	!!source && 'closed' in source

const nowhere = (): void => undefined

/**
 * The console's end of the link to the presentation window, for as long as a
 * presentation runs: the store's state goes out, and what the presentation
 * window reports comes back as machine events, media progress and commands.
 */
export const useConsoleLink = ({
	liveSessionId,
	presentation,
	relay,
	effects,
	send,
	outbox,
	onMedia,
	onCommand,
	onSpeakers,
}: Options): void => {
	const store = useStore<RootState>()
	const handlers = useRef({ onMedia, onCommand, onSpeakers })
	handlers.current = { onMedia, onCommand, onSpeakers }

	useEffect(() => {
		if (!presentation) {
			return
		}
		const channel = openBroadcastChannel(
			bridgeChannelName(liveSessionId, presentation)
		)
		const host = startHost({
			store,
			channel,
			address: { link: presentation, build: Config.VERSION },
			policy: ROOT_SYNC_POLICY,
			locks: webLocks(),
			onMessage: payload => {
				const message = readProjectorMessage(payload)
				switch (message?.kind) {
					case 'display':
						send({
							type: 'projector-display',
							display: message.display,
							screenLabel: message.screenLabel,
						})
						return
					case 'media':
						handlers.current.onMedia(message.media)
						return
					case 'command':
						handlers.current.onCommand(message.command)
						return
					case 'speakers':
						handlers.current.onSpeakers(message.fellBack)
						return
				}
			},
			onPeer: present => send({ type: 'projector-present', present }),
			onOtherBuild: () => send({ type: 'other-build' }),
			onDropped: (key, error) =>
				log.error(`Slice "${key}" cannot reach the presentation window`, error),
		})
		const detach = relay?.attach(host)
		outbox.current = message => host.post(message)

		// A console that reloaded learns which window to hand full screen to.
		const onWindowMessage = (event: MessageEvent<unknown>): void => {
			if (
				event.origin === window.location.origin &&
				isProjectorHere(event.data, presentation) &&
				isWindowProxy(event.source)
			) {
				effects.found(event.source)
			}
		}
		window.addEventListener('message', onWindowMessage)

		return () => {
			window.removeEventListener('message', onWindowMessage)
			outbox.current = nowhere
			detach?.()
			host.stop()
			channel.close()
		}
	}, [effects, liveSessionId, outbox, presentation, relay, send, store])
}
